import lod_ from "lodash";
import { Badge, Dialog, DialogActions, DialogContent, Icon } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import { useState } from "react";
import { t } from "i18next";
import FiltersDndList from "./FiltersDndList";
import MDTypography from "components/Basics/MDTypography";
import DictionaryMenu from "pages/settings/filters/DictionaryMenu";
import { useDispatch, useSelector } from "react-redux";
import { clearPageFilters } from "redux-react/reducers/filtersReducers";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";

/**
 * Filters dialog component
 * This component allows users to add and manage filters for a resource.
 * It displays a list of filters, allows adding new filters from a dictionary,
 * and provides options to save or cancel changes.
 * @param {Object} props - The properties for the FiltersDialog component.
 * @param {Object} props.filtersDialog - The state of the filters dialog (open/closed).
 * @param {Function} props.setFiltersDialog - Function to update the filters dialog state
 * @param {Object} props.dictionary - The dictionary object containing filter definitions.
 * @param {Array} props.filters - The initial list of filters to display.
 * @param {Function} props.onSave - Callback function to handle saving filters.
 * @param {string} props.page - The current page route used to identify filters in the store.
 * @returns {JSX.Element} The rendered FiltersDialog component.
 */
const FiltersDialog = ({
	filtersDialog,
	setFiltersDialog,
	dictionary = {},
	filters: resourcefilters = [],
	onSave = () => {},
	page
}) => {
	const dispatch = useDispatch();
	const profile = useSelector(selectCurrentProfile);
	const [filters, setFilters] = useState(resourcefilters);
	const [addFilterAnchorEl, setAddFilterAnchorEl] = useState(null);

	/**
	 * Add a new filter to the list
	 * @param {string} fullDictionaryPath - The path of the filter in the dictionary
	 * @returns {void}
	 */
	const addNewFilter = fullDictionaryPath => {
		let dictionaryCode = fullDictionaryPath.split(".")[0];

		const dictionaryOfElement = lod_.get(dictionary, dictionaryCode);
		const source = dictionaryOfElement?.source;

		let path = lod_.cloneDeep(fullDictionaryPath);
		if (path.split(".").length > 1) {
			path = path.split(".").slice(1).join(".");
		}

		// Try to get the item in the dictionary
		const dictionaryItem = lod_.get(
			dictionaryOfElement?.items || {},
			path.replaceAll(".", ".items."),
			{}
		);

		if (lod_.isEmpty(dictionaryItem) && source !== "special") {
			return;
		}

		setAddFilterAnchorEl(null);
		let same = filters.find(f => f.path === path);
		if (!same) {
			let filter = {
				dictionary: dictionaryCode,
				path,
				source: source === "special" ? source : "dictionary"
			};

			if (source === "special") {
				filter.type = dictionaryOfElement.type;
			}

			setFilters(prev => [...filters, filter]);
		}
	};

	/**
	 * When editing a filter
	 * @param {number} index - The index of the filter in the list
	 * @param {object} edit - The editions to apply
	 */
	const onEdit = (index, edit) => {
		let clonedFilters = lod_.cloneDeep(filters);
		let item = clonedFilters[index];
		item = lod_.merge(item, edit);
		clonedFilters[index] = item;
		setFilters(clonedFilters);
	};

	/**
	 * Remove a link from a filter
	 * @param {number} itemItem - The index of the filter in the list
	 * @param {number} linkIndex - The index of the link in the filter
	 */
	const removeLink = (itemItem, linkIndex) => {
		let clonedFilters = lod_.cloneDeep(filters);
		let item = clonedFilters[itemItem];
		item.links.splice(linkIndex, 1);
		clonedFilters[itemItem] = item;
		setFilters(clonedFilters);
	};

	/**
	 * Add a link to a filter
	 * @param {object} item - The filter to link
	 * @param {number} index - The index of the filter in the list
	 * @param {string} fullDictionaryPath - The path of the filter in the dictionary
	 */
	const linkItem = (item, index, fullDictionaryPath) => {
		let dictionaryCode = fullDictionaryPath.split(".")[0];

		const dictionaryItem = lod_.get(dictionary, dictionaryCode);
		const source = dictionaryItem?.source;

		let path = lod_.cloneDeep(fullDictionaryPath);
		if (path.split(".").length > 1) {
			path = path.split(".").slice(1).join(".");
		}

		setAddFilterAnchorEl(null);

		let link = {
			dictionary: dictionaryCode,
			path,
			source: source === "special" ? source : "dictionary"
		};

		item.links = item.links || [];

		let same = item.links.find(l => `${l.dictionaryCode}.${l.path}` === fullDictionaryPath);
		if (!same) {
			item.links.push(link);
		}

		let clonedFilters = lod_.cloneDeep(filters);
		clonedFilters[index] = item;
		setFilters(clonedFilters);
	};

	return (
		<Dialog fullWidth maxWidth="xl" open={filtersDialog.open}>
			<DialogContent>
				<MDBox
					display="flex"
					flexDirection="row"
					justifyContent="space-between"
					alignItems="center"
					sx={{ mb: 1 }}
				>
					<MDTypography variant="h6">{t("SETTINGS.FILTERS.addFilters")}</MDTypography>
					<MDBox display="flex" alignItems="center" gap={1}>
						<MDButton
							variant="outlined"
							color="error"
							iconOnly
							size="small"
							onClick={() => {
								if (page && profile?.assistantID) {
									dispatch(clearPageFilters({ assistantID: profile.assistantID, page }));
								}
							}}
						>
							<Icon>clear</Icon>
						</MDButton>
						<MDButton variant="gradient" color="info" onClick={e => setAddFilterAnchorEl(e.target)}>
							<Icon>add</Icon>&nbsp;Ajouter
						</MDButton>
						<DictionaryMenu
							dictionary={dictionary}
							anchorEl={addFilterAnchorEl}
							handleInsertText={addNewFilter}
							handleClose={() => setAddFilterAnchorEl(null)}
							clickableLevels={false}
						/>
					</MDBox>
				</MDBox>
				<FiltersDndList
					dictionary={dictionary}
					items={filters}
					onChange={newList => {
						setFilters(prev => newList);
					}}
					deleteItem={index => {
						setFilters(prev => {
							let newList = [...prev];
							newList.splice(index, 1);
							return newList;
						});
					}}
					linkItem={linkItem}
					removeLink={removeLink}
					onEdit={onEdit}
				/>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={() => setFiltersDialog({ open: false })}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton
					variant="contained"
					color="info"
					onClick={() => {
						onSave(filters);
					}}
				>
					{t("SETTINGS.save")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default FiltersDialog;
